// New-customer modal — opened from the chrome's "+ New customer" quick action.
//
// Backend: POST /api/crm/contacts (kitchenos-api/modules/crm.js). On a fresh
// contact the server dispatches the welcome SMS (Twilio) + email (Brevo)
// best-effort; the modal does not wait on those. On success we fire a
// `tkos:customer-created` CustomEvent on `window` so any open CRM page can
// refresh its data without a full reload.

import { ApiError, api } from "@tkos/api-client";
import type { ToastKind } from "./types";

import "./new-customer-modal.css";

interface OpenOptions {
  showToast: (msg: string, kind?: ToastKind) => void;
}

interface AddressJson {
  line1?: string;
  line2?: string;
  city?: string;
  county?: string;
  postcode?: string;
}

interface ContactPayload {
  name: string;
  email?: string;
  phone?: string;
  address?: AddressJson;
  stage: "lead" | "qualified" | "quoted" | "won" | "lost";
  notes?: string;
  budget?: string;
}

interface PostResponse {
  ok?: boolean;
  contacts?: Array<{ id?: string; name?: string }>;
  error?: string;
}

// Singleton dialog reused across opens to avoid re-creating DOM each time.
let dialog: HTMLDialogElement | null = null;
let isOpen = false;

function buildDialog(): HTMLDialogElement {
  const d = document.createElement("dialog");
  d.className = "tkos-nc";
  d.innerHTML = `
    <form class="tkos-nc__form" method="dialog" novalidate>
      <header class="tkos-nc__head">
        <h2 class="tkos-nc__title">Add a new customer</h2>
        <button type="button" class="tkos-nc__close" data-role="close" aria-label="Close">&times;</button>
      </header>
      <div class="tkos-nc__body">
        <div class="tkos-nc__row">
          <label class="tkos-nc__field tkos-nc__field--wide">
            <span class="tkos-nc__label">Full name <span class="tkos-nc__req">*</span></span>
            <input type="text" name="name" autocomplete="name" required maxlength="200" />
          </label>
        </div>
        <div class="tkos-nc__row tkos-nc__row--two">
          <label class="tkos-nc__field">
            <span class="tkos-nc__label">Email <span class="tkos-nc__req">*</span></span>
            <input type="email" name="email" autocomplete="email" required maxlength="200" />
          </label>
          <label class="tkos-nc__field">
            <span class="tkos-nc__label">Phone</span>
            <input type="tel" name="phone" autocomplete="tel" maxlength="60" placeholder="07…" />
          </label>
        </div>
        <fieldset class="tkos-nc__group">
          <legend class="tkos-nc__legend">Address (optional)</legend>
          <label class="tkos-nc__field tkos-nc__field--wide">
            <span class="tkos-nc__label">Line 1</span>
            <input type="text" name="line1" autocomplete="address-line1" maxlength="200" />
          </label>
          <label class="tkos-nc__field tkos-nc__field--wide">
            <span class="tkos-nc__label">Line 2</span>
            <input type="text" name="line2" autocomplete="address-line2" maxlength="200" />
          </label>
          <div class="tkos-nc__row tkos-nc__row--three">
            <label class="tkos-nc__field">
              <span class="tkos-nc__label">Town / city</span>
              <input type="text" name="city" autocomplete="address-level2" maxlength="120" />
            </label>
            <label class="tkos-nc__field">
              <span class="tkos-nc__label">County</span>
              <input type="text" name="county" autocomplete="address-level1" maxlength="120" />
            </label>
            <label class="tkos-nc__field">
              <span class="tkos-nc__label">Postcode</span>
              <input type="text" name="postcode" autocomplete="postal-code" maxlength="20" />
            </label>
          </div>
        </fieldset>
        <div class="tkos-nc__row tkos-nc__row--two">
          <label class="tkos-nc__field">
            <span class="tkos-nc__label">Pipeline stage</span>
            <select name="stage">
              <option value="lead" selected>Lead</option>
              <option value="qualified">Qualified</option>
              <option value="quoted">Quoted</option>
              <option value="won">Won</option>
              <option value="lost">Lost</option>
            </select>
          </label>
          <label class="tkos-nc__field">
            <span class="tkos-nc__label">Budget (£)</span>
            <input type="text" name="budget" inputmode="numeric" maxlength="20" placeholder="e.g. 12000" />
          </label>
        </div>
        <label class="tkos-nc__field tkos-nc__field--wide">
          <span class="tkos-nc__label">Notes</span>
          <textarea name="notes" rows="3" maxlength="2000"></textarea>
        </label>
        <div class="tkos-nc__error" data-role="error" hidden></div>
      </div>
      <footer class="tkos-nc__foot">
        <button type="button" class="tkos-nc__btn" data-role="cancel">Cancel</button>
        <button type="submit" class="tkos-nc__btn tkos-nc__btn--primary" data-role="submit">
          Add customer
        </button>
      </footer>
    </form>
  `;
  document.body.appendChild(d);
  return d;
}

function fieldValue(form: HTMLFormElement, name: string): string {
  const el = form.elements.namedItem(name);
  if (!el) return "";
  if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement || el instanceof HTMLSelectElement) {
    return el.value.trim();
  }
  return "";
}

function buildPayload(form: HTMLFormElement): ContactPayload | { error: string } {
  const name = fieldValue(form, "name");
  if (!name) return { error: "Full name and email are required." };

  const email = fieldValue(form, "email");
  if (!email) return { error: "Full name and email are required." };
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    return { error: "Please enter a valid email address." };
  }

  const phone = fieldValue(form, "phone");

  const addr: AddressJson = {};
  const line1 = fieldValue(form, "line1");
  const line2 = fieldValue(form, "line2");
  const city = fieldValue(form, "city");
  const county = fieldValue(form, "county");
  const postcode = fieldValue(form, "postcode");
  if (line1) addr.line1 = line1;
  if (line2) addr.line2 = line2;
  if (city) addr.city = city;
  if (county) addr.county = county;
  if (postcode) addr.postcode = postcode;

  const stageRaw = fieldValue(form, "stage");
  const stage = (
    ["lead", "qualified", "quoted", "won", "lost"].includes(stageRaw)
      ? stageRaw
      : "lead"
  ) as ContactPayload["stage"];

  const notes = fieldValue(form, "notes");
  const budget = fieldValue(form, "budget");

  const payload: ContactPayload = { name, stage };
  if (email) payload.email = email;
  if (phone) payload.phone = phone;
  if (Object.keys(addr).length > 0) payload.address = addr;
  if (notes) payload.notes = notes;
  if (budget) payload.budget = budget;
  return payload;
}

function resetForm(form: HTMLFormElement): void {
  form.reset();
  const err = form.querySelector<HTMLElement>("[data-role='error']");
  if (err) {
    err.hidden = true;
    err.textContent = "";
  }
}

export function openNewCustomerModal(opts: OpenOptions): void {
  if (!dialog) dialog = buildDialog();
  if (isOpen) return;
  isOpen = true;

  const form = dialog.querySelector<HTMLFormElement>("form")!;
  const errEl = dialog.querySelector<HTMLElement>("[data-role='error']")!;
  const submitBtn = dialog.querySelector<HTMLButtonElement>("[data-role='submit']")!;
  const cancelBtn = dialog.querySelector<HTMLButtonElement>("[data-role='cancel']")!;
  const closeBtn = dialog.querySelector<HTMLButtonElement>("[data-role='close']")!;

  const close = (): void => {
    if (!isOpen) return;
    isOpen = false;
    if (dialog!.open) dialog!.close();
    cleanup();
  };

  const onCancel = (): void => close();
  const onBackdrop = (e: MouseEvent): void => {
    if (e.target === dialog) close();
  };
  const onSubmit = async (e: SubmitEvent): Promise<void> => {
    e.preventDefault();
    errEl.hidden = true;
    errEl.textContent = "";

    const result = buildPayload(form);
    if ("error" in result) {
      errEl.textContent = result.error;
      errEl.hidden = false;
      return;
    }

    submitBtn.disabled = true;
    submitBtn.textContent = "Adding…";
    try {
      const resp = await api<PostResponse>("/crm/contacts", {
        method: "POST",
        body: JSON.stringify({ contact: result }),
      });
      if (!resp || resp.ok === false) {
        throw new Error(resp?.error || "Could not add customer.");
      }
      const created = resp.contacts?.[0];
      opts.showToast(
        result.email || result.phone
          ? `Customer added — welcome ${result.email && result.phone ? "SMS + email" : result.email ? "email" : "SMS"} queued.`
          : "Customer added.",
        "success",
      );
      // Broadcast so the CRM page (or any other listener) can refresh.
      window.dispatchEvent(
        new CustomEvent("tkos:customer-created", { detail: { contact: created } }),
      );
      resetForm(form);
      close();
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? `Server error (HTTP ${err.status})`
          : err instanceof Error
            ? err.message
            : "Could not add customer.";
      errEl.textContent = msg;
      errEl.hidden = false;
      opts.showToast(msg, "error");
    } finally {
      submitBtn.disabled = false;
      submitBtn.textContent = "Add customer";
    }
  };

  function cleanup(): void {
    form.removeEventListener("submit", onSubmit);
    cancelBtn.removeEventListener("click", onCancel);
    closeBtn.removeEventListener("click", onCancel);
    dialog!.removeEventListener("click", onBackdrop);
    dialog!.removeEventListener("cancel", onCancel);
  }

  form.addEventListener("submit", onSubmit);
  cancelBtn.addEventListener("click", onCancel);
  closeBtn.addEventListener("click", onCancel);
  dialog.addEventListener("click", onBackdrop);
  // <dialog> default "cancel" event fires on Escape.
  dialog.addEventListener("cancel", onCancel);

  if (typeof dialog.showModal === "function") {
    dialog.showModal();
  } else {
    dialog.setAttribute("open", "");
  }
  // Focus the first input on next tick (after showModal lays out the dialog).
  window.setTimeout(() => {
    const first = form.querySelector<HTMLInputElement>("input[name='name']");
    first?.focus();
  }, 30);
}
